import { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import AuthProvider from './contexts/AuthProvider';
import DevAuthProvider from './contexts/DevAuthProvider';
import PrivateRoute from './components/PrivateRoute';
import AppLayout from './components/layout/AppLayout';
import { TooltipProvider } from './components/ui/tooltip';
import Login from './pages/Login';
import SSOCallback from './pages/SSOCallback';
import Explore from './pages/Explore';
import Substitute from './pages/Substitute';
import Shortlists from './pages/Shortlists';
import ShortlistDetail from './pages/ShortlistDetail';
import Overview from './pages/Overview';
import Guide from './pages/Guide';
import NotFound from './pages/NotFound';
import { DEV_AUTH_BYPASS } from './config';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60_000,
      refetchOnWindowFocus: false,
      retry: (count, err) => count < 1 && !(err?.response?.status >= 400 && err?.response?.status < 500),
    },
  },
});

const Provider = DEV_AUTH_BYPASS ? DevAuthProvider : AuthProvider;

function App() {
  // Light is the primary design: only go dark when explicitly chosen.
  useEffect(() => {
    if (localStorage.getItem('theme') === 'dark') document.documentElement.classList.add('dark');
    else document.documentElement.classList.remove('dark');
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <Provider>
        <TooltipProvider delayDuration={250}>
          <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
            <Routes>
              <Route path="/login" element={DEV_AUTH_BYPASS ? <Navigate to="/explore" replace /> : <Login />} />
              <Route path="/sso-callback" element={DEV_AUTH_BYPASS ? <Navigate to="/explore" replace /> : <SSOCallback />} />
              <Route
                element={
                  <PrivateRoute>
                    <AppLayout />
                  </PrivateRoute>
                }
              >
                {/* One Explore element for both paths so results/scroll survive opening an item */}
                <Route element={<Explore />}>
                  <Route path="/explore" element={null} />
                  <Route path="/item/:odooId" element={null} />
                </Route>
                <Route path="/substitute" element={<Substitute />} />
                <Route path="/shortlists" element={<Shortlists />} />
                <Route path="/shortlists/:id" element={<ShortlistDetail />} />
                <Route path="/overview" element={<Overview />} />
                <Route path="/guide" element={<Guide />} />
                <Route path="/dashboard" element={<Navigate to="/explore" replace />} />
                <Route path="/" element={<Navigate to="/explore" replace />} />
                <Route path="*" element={<NotFound />} />
              </Route>
            </Routes>
          </Router>
        </TooltipProvider>
      </Provider>
    </QueryClientProvider>
  );
}

export default App;
